import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
    Calendar,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    Download,
    Mail,
    Monitor,
    Phone,
    Smartphone,
    Tablet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import SearchInput from "@/components/ui/SearchInput";
import { Button } from "@/components/ui/button";
import { DepartmentFilter } from "@/components/filters/DepartmentFilter";
import { useUserDirectory } from "@/hooks/useUserDirectory";
import {
    fetchUserDirectory,
    type UserDirectoryDevice,
    type UserDirectoryItem,
} from "@/lib/axios/api/user-directory.api";
import * as XLSX from "xlsx";

const PAGE_SIZE = 10;
const EXPORT_PAGE_SIZE = 200;

/** Table header band — slate / blue-grey per directory mock */
const tableHeaderBg =
    "bg-[#5a6f82] text-white dark:bg-[#1a3550] dark:text-slate-100";
/** Sticky first column (User): opaque bg + z-index so horizontal scroll only moves other columns */
const stickyUserTh = cn(
    "sticky left-0 top-0 z-30 w-[min(280px,50vw)] min-w-[min(280px,50vw)] max-w-[min(280px,50vw)] px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider sm:px-3 shadow-[4px_0_12px_-6px_rgba(15,23,42,0.2)] dark:shadow-[4px_0_12px_-6px_rgba(0,0,0,0.35)]",
    tableHeaderBg
);
const tableHeaderTh = cn(
    "sticky top-0 z-20 px-2 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider sm:px-3",
    tableHeaderBg
);
/** Sticky user cells need opaque bg; row uses translucent Tailwind opacity so inherit would show scrolled columns through */
const stickyUserTd =
    "sticky left-0 z-10 w-[min(280px,50vw)] min-w-[min(280px,50vw)] max-w-[min(280px,50vw)] shadow-[4px_0_12px_-8px_rgba(15,23,42,0.08)] dark:shadow-[4px_0_12px_-8px_rgba(0,0,0,0.25)]";

const DIRECTORY_TAG_CLASS =
    "inline-flex max-w-full truncate rounded-md border-0 bg-slate-100 px-2 py-0.5 text-[10px] font-normal text-slate-700 dark:bg-[#1A3547] dark:text-slate-200";

function directoryRowBackgrounds(rowIndex: number, isSelected: boolean) {
    if (isSelected) {
        // Stronger highlight (~30% heavier than teal-50/90) so the active row reads clearly in the grid
        return {
            tr: "bg-teal-100/95 hover:bg-teal-100 dark:bg-teal-950/40 dark:hover:bg-teal-950/52",
            stickyTd:
                "bg-teal-100 group-hover:bg-teal-100 dark:bg-teal-950/50 dark:group-hover:bg-teal-950/55",
            cell: "bg-teal-100/95 group-hover:bg-teal-100 dark:bg-teal-950/40 dark:group-hover:bg-teal-950/52",
        };
    }
    const isEven = rowIndex % 2 === 0;
    if (isEven) {
        return {
            tr: "bg-white hover:bg-slate-50/90 dark:bg-[#071827] dark:hover:bg-[#0c2436]",
            stickyTd:
                "bg-white group-hover:bg-slate-50 dark:bg-[#071827] dark:group-hover:bg-[#0c2436]",
            cell: "bg-white group-hover:bg-slate-50/90 dark:bg-[#071827] dark:group-hover:bg-[#0c2436]",
        };
    }
    return {
        tr: "bg-slate-100/80 hover:bg-slate-100 dark:bg-[#0a2233] dark:hover:bg-[#0d2a3d]",
        stickyTd:
            "bg-slate-100/80 group-hover:bg-slate-100 dark:bg-[#0a2233] dark:group-hover:bg-[#0d2a3d]",
        cell: "bg-slate-100/80 group-hover:bg-slate-100 dark:bg-[#0a2233] dark:group-hover:bg-[#0d2a3d]",
    };
}

/** Only scroll the table overflow panel — `scrollIntoView` also scrolls window and outer layouts. */
function scrollRowIntoScrollContainer(container: HTMLElement, row: HTMLElement) {
    if (!container.contains(row)) return;
    const c = container.getBoundingClientRect();
    const r = row.getBoundingClientRect();
    const deltaY = r.top - c.top - c.height / 2 + r.height / 2;
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const nextTop = Math.min(maxTop, Math.max(0, container.scrollTop + deltaY));
    container.scrollTo({ top: nextTop, behavior: "smooth" });
}

const AVATAR_COLORS = [
    "bg-teal-400",
    "bg-blue-400",
    "bg-violet-400",
    "bg-rose-400",
    "bg-amber-400",
    "bg-emerald-500",
];

function getAvatarColorClass(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getAvatarSource(image?: string): string | undefined {
    if (!image) return undefined;
    const trimmed = image.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith("data:")) return trimmed;
    return `data:image/jpeg;base64,${trimmed}`;
}

function getDeviceIcon(os?: string, sizeClass = "h-3.5 w-3.5") {
    const normalized = (os || "").toLowerCase();
    if (normalized === "windows")
        return <Monitor className={cn(sizeClass, "text-blue-500")} />;
    if (normalized === "macos" || normalized === "apple")
        return <Monitor className={cn(sizeClass, "text-slate-400")} />;
    if (normalized === "ios" || normalized === "iphone")
        return <Smartphone className={cn(sizeClass, "text-slate-400")} />;
    if (normalized === "android")
        return <Smartphone className={cn(sizeClass, "text-green-500")} />;
    if (normalized === "ipad")
        return <Tablet className={cn(sizeClass, "text-slate-400")} />;
    return <Monitor className={cn(sizeClass)} />;
}

function formatDevicesForExport(devices: UserDirectoryDevice[]): string {
    if (!devices || devices.length === 0) return "";
    return devices.map((d) => `${d.name} (${d.os})`).join("; ");
}

function formatDevicesTooltip(devices: UserDirectoryDevice[]): string {
    if (!devices || devices.length === 0) return "";
    return devices.map((d) => `${d.name} (${d.os})`).join("\n");
}

/**
 * Parse a UTC date string from the backend (UTC+0) and return
 * a human-readable relative time label in local browser time.
 */
function formatRelativeTime(utcDateStr: string | null | undefined): string | null {
    if (!utcDateStr) return null;
    // Append 'Z' to treat as UTC if no timezone designator is present
    const normalized =
        utcDateStr.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(utcDateStr)
            ? utcDateStr
            : utcDateStr + "Z";
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return null;

    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return "just now";
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    const diffMonth = Math.floor(diffDay / 30);
    if (diffMonth < 12) return `${diffMonth}mo ago`;
    return `${Math.floor(diffMonth / 12)}y ago`;
}

/**
 * Returns Tailwind-compatible inline style for the badge based on sign-in recency.
 * Green ≤ 1h | Blue ≤ 24h | Orange ≤ 3d | Red > 3d
 */
function getSignInBadgeStyle(utcDateStr: string | null | undefined): React.CSSProperties {
    if (!utcDateStr) return { backgroundColor: "#64748b", color: "#fff" };
    const normalized =
        utcDateStr.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(utcDateStr)
            ? utcDateStr
            : utcDateStr + "Z";
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return { backgroundColor: "#64748b", color: "#fff" };
    const diffMs = Date.now() - date.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays < 1) return { backgroundColor: "#16a34a", color: "#fff" }; // green  — < 1 day
    if (diffDays < 7) return { backgroundColor: "#2563eb", color: "#fff" }; // blue   — < 7 days
    if (diffDays < 30) return { backgroundColor: "#f59e0b", color: "#fff" }; // orange — < 1 month
    if (diffDays < 90) return { backgroundColor: "#eab308", color: "#fff" }; // yellow — < 3 months
    return { backgroundColor: "#ef4444", color: "#fff" };                     // red    — > 3 months
}

/** Format a UTC date string as a readable local datetime for tooltips. */
function formatLocalDateTime(utcDateStr: string | null | undefined): string {
    if (!utcDateStr) return "";
    const normalized =
        utcDateStr.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(utcDateStr)
            ? utcDateStr
            : utcDateStr + "Z";
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return "";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    return `${dd}/${mm}/${yyyy}, ${time}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// UserCard – left panel
// ─────────────────────────────────────────────────────────────────────────────
const UserCard: React.FC<{
    user: UserDirectoryItem;
    isSelected: boolean;
    onClick: () => void;
}> = ({ user, isSelected, onClick }) => {
    const avatarSrc = getAvatarSource(user.image);
    const colorClass = getAvatarColorClass(user.displayName || "");

    return (
        <button
            onClick={onClick}
            className={cn(
                "w-full text-left rounded-lg border p-2 transition-all sm:p-2.5",
                isSelected
                    ? "border-teal-400 bg-teal-50/60 dark:bg-teal-900/20 dark:border-teal-400"
                    : "border-transparent hover:bg-slate-50 dark:hover:bg-[#0E2636]"
            )}
        >
            <div className="flex gap-3">
                {avatarSrc ? (
                    <img
                        src={avatarSrc}
                        alt={user.displayName}
                        className="h-10 w-10 rounded-full object-cover flex-shrink-0 border border-gray-200"
                    />
                ) : (
                    <div
                        className={cn(
                            "h-10 w-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 text-white",
                            colorClass
                        )}
                    >
                        {user.displayName?.slice(0, 2)?.toUpperCase() || "NA"}
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
                        <span className="shrink-0 text-[13px] font-semibold leading-tight text-slate-900 dark:text-white">
                            {user.displayName}
                        </span>
                        {user.jobTitle && (
                            <span
                                className="max-w-[min(100%,12rem)] truncate text-[11px] font-normal leading-tight text-slate-500 dark:text-slate-400"
                                title={user.jobTitle}
                            >
                                {user.jobTitle}
                            </span>
                        )}
                        {user.department && (
                            <span className="inline-flex max-w-full truncate rounded px-1 py-0.5 text-[9px] font-medium leading-none text-slate-600 bg-slate-100 dark:bg-[#1A3547] dark:text-slate-300">
                                {user.department}
                            </span>
                        )}
                        {user.branch && (
                            <span className="inline-flex max-w-full truncate rounded px-1 py-0.5 text-[9px] font-medium leading-none text-slate-600 bg-slate-100 dark:bg-[#1A3547] dark:text-slate-300">
                                {user.branch}
                            </span>
                        )}
                        {user.region && (
                            <span className="inline-flex max-w-full truncate rounded px-1 py-0.5 text-[9px] font-medium leading-none text-slate-600 bg-slate-100 dark:bg-[#1A3547] dark:text-slate-300">
                                {user.region}
                            </span>
                        )}
                    </div>
                    <p className="mt-0.5 text-[11px] text-muted-foreground truncate">
                        {user.mail}
                        {user.phoneNumber ? ` · ${user.phoneNumber}` : ""}
                    </p>
                </div>
            </div>
        </button>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SignInBadge – badge with hover tooltip for lastSignInDateTime
// ─────────────────────────────────────────────────────────────────────────────
const SignInBadge: React.FC<{ lastSignInDateTime: string }> = ({ lastSignInDateTime }) => {
    const [showTip, setShowTip] = React.useState(false);
    return (
        <div className="relative flex justify-center">
            <span
                className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium leading-none cursor-default"
                style={getSignInBadgeStyle(lastSignInDateTime)}
                onMouseEnter={() => setShowTip(true)}
                onMouseLeave={() => setShowTip(false)}
            >
                {formatRelativeTime(lastSignInDateTime)}
            </span>
            {showTip && (
                <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-[9999] w-max max-w-[240px]">
                    <div className="bg-white dark:bg-[#FCFCFD] text-slate-900 text-[11px] leading-snug rounded-lg px-3 py-2 shadow-lg border border-slate-200">
                        <p className="font-semibold text-slate-500 mb-0.5 whitespace-nowrap text-[10px] uppercase tracking-wide">Last sign-in</p>
                        <p className="text-slate-900 font-medium whitespace-nowrap">{formatLocalDateTime(lastSignInDateTime)}</p>
                    </div>
                    {/* Arrow pointing down */}
                    <div className="flex justify-center -mt-px">
                        <div className="w-2.5 h-2.5 bg-white dark:bg-[#FCFCFD] border-r border-b border-slate-200 rotate-45 -mt-[5px]" />
                    </div>
                </div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// UserDirectoryProfileHeader — gradient band (right panel, above table)
// ─────────────────────────────────────────────────────────────────────────────
const UserDirectoryProfileHeader: React.FC<{ user: UserDirectoryItem }> = ({ user }) => {
    const avatarSrc = getAvatarSource(user.image);
    const colorClass = getAvatarColorClass(user.displayName || "");

    return (
        <div className="shrink-0 border-y dark:border-[#122638]">
            <div className="bg-gradient-to-r from-slate-100 via-blue-50 to-blue-100 dark:from-[#0E2636] dark:via-[#0A2E4A] dark:to-[#0A3050] px-3 py-2.5 sm:px-4 sm:py-3 flex items-center gap-3">
                {avatarSrc ? (
                    <img
                        src={avatarSrc}
                        alt={user.displayName}
                        className="h-11 w-11 sm:h-12 sm:w-12 rounded-full object-cover border-2 border-white/60 flex-shrink-0 shadow"
                    />
                ) : (
                    <div
                        className={cn(
                            "h-11 w-11 sm:h-12 sm:w-12 rounded-full flex items-center justify-center text-lg sm:text-xl font-bold flex-shrink-0 border-2 border-white/40 shadow text-white",
                            colorClass
                        )}
                    >
                        {user.displayName?.slice(0, 2)?.toUpperCase() || "NA"}
                    </div>
                )}
                <div className="min-w-0">
                    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <h4 className="m-0 shrink-0 text-sm font-bold leading-tight text-slate-900 dark:text-white sm:text-base">
                            {user.displayName}
                        </h4>
                        {user.jobTitle && (
                            <span className="inline-flex max-w-full truncate rounded-full border border-slate-200/60 bg-white/70 px-2 py-0.5 text-[10px] text-slate-700 dark:border-white/15 dark:bg-white/10 dark:text-slate-200 sm:text-[11px]">
                                {user.jobTitle}
                            </span>
                        )}
                        {user.department && (
                            <span className="inline-flex max-w-full truncate rounded-full border border-slate-200/60 bg-white/70 px-2 py-0.5 text-[10px] text-slate-700 dark:border-white/15 dark:bg-white/10 dark:text-slate-200 sm:text-[11px]">
                                {user.department}
                            </span>
                        )}
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-[11px] text-slate-600 dark:text-slate-300 sm:text-xs">
                        {user.mail}
                        {user.phoneNumber ? ` · ${user.phoneNumber}` : ""}
                    </p>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// UserDirectoryStatWidgets — four stats in one row (right panel, below table)
// ─────────────────────────────────────────────────────────────────────────────
const UserDirectoryStatWidgets: React.FC<{ user: UserDirectoryItem }> = ({ user }) => {
    const devices = user.device ?? [];
    const [deviceIdx, setDeviceIdx] = React.useState(0);
    const [slideDir, setSlideDir] = React.useState<"left" | "right" | null>(null);
    const [animKey, setAnimKey] = React.useState(0);

    React.useEffect(() => {
        setDeviceIdx(0);
        setSlideDir(null);
    }, [user.id]);

    const currentDevice = devices[deviceIdx];
    const hasMultiple = devices.length > 1;

    const navigate = (dir: "left" | "right") => {
        setSlideDir(dir);
        setAnimKey((k) => k + 1);
        setDeviceIdx((i) =>
            dir === "right" ? (i + 1) % devices.length : (i - 1 + devices.length) % devices.length
        );
    };

    const slideStyle: React.CSSProperties = slideDir
        ? {
            animation: `slideIn${slideDir === "right" ? "FromRight" : "FromLeft"} 0.35s cubic-bezier(0.25,0.8,0.25,1) both`,
        }
        : {};

    const statCellClass =
        "flex h-full min-h-0 min-w-0 flex-col gap-1.5 px-1 py-[0.65rem] sm:px-2 sm:py-[0.65rem]";
    const statTitleRowClass = "flex shrink-0 min-w-0 w-full items-center justify-center gap-1.5";
    const statBodyClass =
        "flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-1 text-center";

    return (
        <div className="shrink-0 border-t bg-white dark:border-[#122638] dark:bg-[#071827]">
            <div className="grid grid-cols-4 divide-x divide-border/80 items-stretch dark:divide-[#122638]">
                <div className={statCellClass}>
                    <div className={statTitleRowClass}>
                        <CalendarDays className="h-4 w-4 shrink-0 text-blue-400" />
                        <span className="min-w-0 text-left text-[8px] font-medium leading-tight text-muted-foreground sm:text-[10px]">
                            Meetings Joined
                        </span>
                    </div>
                    <div className={statBodyClass}>
                        <span className="text-base font-bold leading-none text-slate-900 dark:text-white sm:text-lg">
                            {user.meetingJoined ?? 0}
                        </span>
                    </div>
                </div>
                <div className={statCellClass}>
                    <div className={statTitleRowClass}>
                        <Calendar className="h-4 w-4 shrink-0 text-emerald-400" />
                        <span className="min-w-0 text-left text-[8px] font-medium leading-tight text-muted-foreground sm:text-[10px]">
                            Meetings Organized
                        </span>
                    </div>
                    <div className={statBodyClass}>
                        <span className="text-base font-bold leading-none text-slate-900 dark:text-white sm:text-lg">
                            {user.meetingOrganized ?? 0}
                        </span>
                    </div>
                </div>
                <div className={statCellClass}>
                    <div className="flex max-w-full shrink-0 flex-wrap items-center justify-center gap-1">
                        {hasMultiple && (
                            <button
                                type="button"
                                onClick={() => navigate("left")}
                                className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-[#122638] transition-colors"
                                aria-label="Previous device"
                            >
                                <ChevronLeft className="h-3 w-3 text-emerald-500 sm:h-3.5 sm:w-3.5" />
                            </button>
                        )}
                        <div key={animKey} style={slideStyle} className="flex shrink-0 items-center gap-1">
                            {getDeviceIcon(currentDevice?.os, "h-4 w-4 sm:h-4 sm:w-4")}
                            <span className="text-[8px] font-medium leading-none text-muted-foreground sm:text-[10px]">
                                Device
                            </span>
                        </div>
                        {hasMultiple && (
                            <button
                                type="button"
                                onClick={() => navigate("right")}
                                className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-slate-200 dark:hover:bg-[#122638] transition-colors"
                                aria-label="Next device"
                            >
                                <ChevronRight className="h-3 w-3 text-emerald-500 sm:h-3.5 sm:w-3.5" />
                            </button>
                        )}
                    </div>
                    <div className={statBodyClass}>
                        <div key={`name-${animKey}`} style={slideStyle} className="flex w-full min-w-0 flex-col items-center gap-1">
                            <div className="flex max-w-full flex-wrap items-center justify-center gap-1.5">
                                <span
                                    className="truncate text-center text-[10px] font-semibold text-slate-900 dark:text-white sm:text-xs"
                                    title={currentDevice?.name}
                                >
                                    {currentDevice?.name ?? "N/A"}
                                </span>
                                {currentDevice?.lastSignInDateTime && (
                                    <SignInBadge lastSignInDateTime={currentDevice.lastSignInDateTime} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={statCellClass}>
                    <div className={statTitleRowClass}>
                        <Mail className="h-4 w-4 shrink-0 text-amber-400" />
                        <span className="min-w-0 text-left text-[8px] font-medium leading-tight text-muted-foreground sm:text-[10px]">
                            Voicemails
                        </span>
                    </div>
                    <div className={statBodyClass}>
                        <span className="text-base font-bold leading-none text-slate-900 dark:text-white sm:text-lg">
                            {user.voicemail ?? 0}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

const USER_DIRECTORY_TABLE_COL_COUNT = 10;

// ─────────────────────────────────────────────────────────────────────────────
// UserDirectoryTable — bottom panel grid (directory mock)
// ─────────────────────────────────────────────────────────────────────────────
const UserDirectoryTable: React.FC<{
    rows: UserDirectoryItem[];
    loading: boolean;
    error: string | null;
    selectedUserId: string | null | undefined;
    tableScrollRef: React.RefObject<HTMLDivElement | null>;
    canPanTableX: boolean;
    isPanningTable: boolean;
    onTablePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onTablePointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onTablePointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
    onTablePointerCancel: (e: React.PointerEvent<HTMLDivElement>) => void;
    onTableLostPointerCapture: () => void;
}> = ({
    rows,
    loading,
    error,
    selectedUserId,
    tableScrollRef,
    canPanTableX,
    isPanningTable,
    onTablePointerDown,
    onTablePointerMove,
    onTablePointerUp,
    onTablePointerCancel,
    onTableLostPointerCapture,
}) => (
        <div
            ref={tableScrollRef}
            role="region"
            aria-label="User directory table. Drag horizontally or use the scrollbar to see all columns."
            className={cn(
                "min-h-0 flex-1 touch-pan-x overflow-x-auto overflow-y-auto overscroll-x-contain pb-2",
                "scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 dark:scrollbar-track-gray-800 dark:scrollbar-thumb-gray-600",
                "[&::-webkit-scrollbar-corner]:bg-transparent",
                canPanTableX && !isPanningTable && "cursor-grab",
                isPanningTable && "cursor-grabbing select-none"
            )}
            onPointerDown={onTablePointerDown}
            onPointerMove={onTablePointerMove}
            onPointerUp={onTablePointerUp}
            onPointerCancel={onTablePointerCancel}
            onLostPointerCapture={onTableLostPointerCapture}
        >
            <table className="min-w-[max(100%,880px)] w-full border-separate border-spacing-0 table-auto caption-bottom text-xs isolate">
                <thead>
                    <tr>
                        <th scope="col" className={cn(stickyUserTh, "backdrop-blur-sm")}>
                            User
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "backdrop-blur-sm")}>
                            Branch
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "backdrop-blur-sm")}>
                            Department
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "backdrop-blur-sm")}>
                            Phone
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "backdrop-blur-sm")}>
                            City
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "backdrop-blur-sm")}>
                            Region
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "backdrop-blur-sm")}>
                            Device
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "text-center backdrop-blur-sm")}>
                            Mtg Joined
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "text-center backdrop-blur-sm")}>
                            Mtg Organized
                        </th>
                        <th scope="col" className={cn(tableHeaderTh, "text-center backdrop-blur-sm")}>
                            Voicemails
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        [...Array(PAGE_SIZE)].map((_, i) => (
                            <tr
                                key={i}
                                className={cn(
                                    "border-b border-border/60 dark:border-[#122638]",
                                    i % 2 === 0 ? "bg-white dark:bg-[#071827]" : "bg-slate-100/80 dark:bg-[#0a2233]"
                                )}
                            >
                                {[...Array(USER_DIRECTORY_TABLE_COL_COUNT)].map((_, j) => (
                                    <td
                                        key={j}
                                        className={cn(
                                            "px-2 py-2 sm:px-3",
                                            j === 0 &&
                                            cn(
                                                stickyUserTd,
                                                i % 2 === 0
                                                    ? "bg-white dark:bg-[#071827]"
                                                    : "bg-slate-100/80 dark:bg-[#0a2233]"
                                            )
                                        )}
                                    >
                                        <div
                                            className="h-3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse"
                                            style={{ width: `${50 + (j * 13 + i * 7) % 40}%` }}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : error ? (
                        <tr>
                            <td colSpan={USER_DIRECTORY_TABLE_COL_COUNT} className="py-10 text-center text-xs text-red-500">
                                {error}
                            </td>
                        </tr>
                    ) : rows.length === 0 ? (
                        <tr>
                            <td colSpan={USER_DIRECTORY_TABLE_COL_COUNT} className="py-10 text-center text-xs text-muted-foreground">
                                No user directory data found
                            </td>
                        </tr>
                    ) : (
                        rows.map((row, rowIndex) => {
                            const avatarSrc = getAvatarSource(row.image);
                            const colorClass = getAvatarColorClass(row.displayName || "");
                            const isSelected = selectedUserId === row.id;
                            const bg = directoryRowBackgrounds(rowIndex, isSelected);
                            return (
                                <tr
                                    key={row.id}
                                    data-user-row-id={row.id}
                                    className={cn("group border-b border-slate-200/80 dark:border-[#122638]", bg.tr)}
                                >
                                    <td
                                        className={cn(
                                            stickyUserTd,
                                            "px-2 py-2 sm:px-3 align-middle",
                                            bg.stickyTd
                                        )}
                                    >
                                        <div className="flex min-w-0 max-w-[min(320px,46vw)] items-center gap-2">
                                            {avatarSrc ? (
                                                <img
                                                    src={avatarSrc}
                                                    alt={row.displayName}
                                                    className="h-8 w-8 flex-shrink-0 rounded-full border border-gray-200 object-cover"
                                                />
                                            ) : (
                                                <div
                                                    className={cn(
                                                        "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white",
                                                        colorClass
                                                    )}
                                                >
                                                    {row.displayName?.slice(0, 2)?.toUpperCase() || "NA"}
                                                </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div
                                                    className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0"
                                                    title={
                                                        row.jobTitle
                                                            ? `${row.displayName} — ${row.jobTitle}`
                                                            : row.displayName
                                                    }
                                                >
                                                    <span className="truncate text-[12px] font-semibold text-slate-900 dark:text-white">
                                                        {row.displayName}
                                                    </span>
                                                    {row.jobTitle ? (
                                                        <span className="max-w-full truncate text-[10px] font-normal text-slate-500 dark:text-slate-400">
                                                            {row.jobTitle}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <span
                                                    className="mt-0.5 block truncate text-[10px] text-muted-foreground"
                                                    title={row.mail}
                                                >
                                                    {row.mail}
                                                </span>
                                            </div>
                                        </div>
                                    </td>

                                    <td className={cn("px-2 py-2 sm:px-3 align-middle", bg.cell)}>
                                        {row.branch ? (
                                            <span className={DIRECTORY_TAG_CLASS} title={row.branch}>
                                                {row.branch}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">N/A</span>
                                        )}
                                    </td>

                                    <td className={cn("px-2 py-2 sm:px-3 align-middle", bg.cell)}>
                                        {row.department ? (
                                            <span className={DIRECTORY_TAG_CLASS} title={row.department}>
                                                {row.department}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">N/A</span>
                                        )}
                                    </td>

                                    <td className={cn("px-2 py-2 sm:px-3 align-middle whitespace-nowrap", bg.cell)}>
                                        {row.phoneNumber ? (
                                            <span className="flex items-center gap-1.5 text-slate-800 dark:text-slate-100">
                                                <Phone className="h-3.5 w-3.5 flex-shrink-0 text-slate-500 dark:text-slate-400" />
                                                {row.phoneNumber}
                                            </span>
                                        ) : (
                                            <span className="text-muted-foreground">N/A</span>
                                        )}
                                    </td>

                                    <td className={cn("px-2 py-2 sm:px-3 align-middle", bg.cell)}>
                                        <span className="block max-w-[100px] truncate" title={row.city}>
                                            {row.city || "N/A"}
                                        </span>
                                    </td>

                                    <td className={cn("px-2 py-2 sm:px-3 align-middle", bg.cell)}>
                                        <span className="block max-w-[100px] truncate" title={row.region}>
                                            {row.region || "N/A"}
                                        </span>
                                    </td>

                                    <td
                                        className={cn("px-2 py-2 sm:px-3 align-middle", bg.cell)}
                                        title={formatDevicesTooltip(row.device ?? [])}
                                    >
                                        {row.device && row.device.length > 0 ? (
                                            <div className="flex max-w-[180px] items-center gap-1.5">
                                                {getDeviceIcon(row.device[0].os, "h-3.5 w-3.5 flex-shrink-0 text-slate-500 dark:text-slate-400")}
                                                <span className="min-w-0 truncate text-left text-[11px] leading-tight text-slate-800 dark:text-slate-100">
                                                    {row.device[0].name}
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="flex items-center gap-1.5 text-muted-foreground">
                                                <Monitor className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
                                                N/A
                                            </span>
                                        )}
                                    </td>

                                    <td className={cn("px-2 py-2 text-center tabular-nums sm:px-3 align-middle", bg.cell)}>
                                        {row.meetingJoined ?? 0}
                                    </td>

                                    <td className={cn("px-2 py-2 text-center tabular-nums sm:px-3 align-middle", bg.cell)}>
                                        {row.meetingOrganized ?? 0}
                                    </td>

                                    <td className={cn("px-2 py-2 text-center tabular-nums sm:px-3 align-middle", bg.cell)}>
                                        {row.voicemail ?? 0}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
interface UserDirectorySectionProps {
    startDate?: string | null;
    endDate?: string | null;
    agentIds?: string[] | null;
}

const UserDirectorySection: React.FC<UserDirectorySectionProps> = ({ startDate, endDate, agentIds }) => {
    const [userName, setUserName] = useState("");
    const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
    const [page, setPage] = useState(1);
    const [pageInput, setPageInput] = useState("1");
    const [exportLoading, setExportLoading] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserDirectoryItem | null>(null);
    const [canPanTableX, setCanPanTableX] = useState(false);
    const [isPanningTable, setIsPanningTable] = useState(false);
    const tableScrollRef = React.useRef<HTMLDivElement>(null);
    const tablePanPointerId = React.useRef<number | null>(null);
    const tablePanStartX = React.useRef(0);
    const tablePanStartScrollLeft = React.useRef(0);

    const {
        rows,
        totalCount,
        loading,
        error,
        availableDepartments,
        departmentsLoading,
    } = useUserDirectory({
        userName,
        departments: selectedDepartments,
        page,
        pageSize: PAGE_SIZE,
        startTime: startDate,
        endTime: endDate,
        agentIds,
    });

    const updateCanPanTableX = React.useCallback(() => {
        const el = tableScrollRef.current;
        if (!el) return;
        setCanPanTableX(el.scrollWidth > el.clientWidth + 1);
    }, []);

    useLayoutEffect(() => {
        updateCanPanTableX();
    }, [rows, loading, error, updateCanPanTableX]);

    useEffect(() => {
        const el = tableScrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => updateCanPanTableX());
        ro.observe(el);
        return () => ro.disconnect();
    }, [updateCanPanTableX]);

    /** When an agent is selected in the sidebar, scroll their row into view inside the table panel */
    useLayoutEffect(() => {
        if (loading || error) return;
        const id = selectedUser?.id;
        if (!id || !rows.some((r) => r.id === id)) return;
        const root = tableScrollRef.current;
        if (!root) return;
        const raf = requestAnimationFrame(() => {
            const rootNow = tableScrollRef.current;
            if (!rootNow) return;
            const escaped = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(id) : id.replace(/"/g, '\\"');
            const row = rootNow.querySelector<HTMLElement>(`tr[data-user-row-id="${escaped}"]`);
            if (row) scrollRowIntoScrollContainer(rootNow, row);
        });
        return () => cancelAnimationFrame(raf);
    }, [selectedUser?.id, rows, loading, error]);

    const handleTablePointerDown = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (e.button !== 0) return;
        const el = tableScrollRef.current;
        if (!el || el.scrollWidth <= el.clientWidth + 1) return;
        const target = e.target as HTMLElement;
        if (target.closest("a,button,input,textarea,select,[data-no-drag-scroll]")) return;
        tablePanPointerId.current = e.pointerId;
        tablePanStartX.current = e.clientX;
        tablePanStartScrollLeft.current = el.scrollLeft;
        try {
            el.setPointerCapture(e.pointerId);
        } catch {
            tablePanPointerId.current = null;
            return;
        }
        setIsPanningTable(true);
    }, []);

    const handleTablePointerMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (tablePanPointerId.current !== e.pointerId) return;
        const el = tableScrollRef.current;
        if (!el) return;
        const dx = e.clientX - tablePanStartX.current;
        el.scrollLeft = tablePanStartScrollLeft.current - dx;
    }, []);

    const endTablePan = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (tablePanPointerId.current !== e.pointerId) return;
        const el = tableScrollRef.current;
        tablePanPointerId.current = null;
        setIsPanningTable(false);
        try {
            if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
        } catch {
            /* ignore */
        }
    }, []);

    const handleTableLostPointerCapture = React.useCallback(() => {
        tablePanPointerId.current = null;
        setIsPanningTable(false);
    }, []);

    // Reset page on filter change
    useEffect(() => {
        setPage(1);
    }, [userName, selectedDepartments, startDate, endDate, agentIds]);

    // Auto-select first user; keep selection if still visible on new page
    useEffect(() => {
        if (rows.length > 0) {
            const stillVisible = rows.find((r) => r.id === selectedUser?.id);
            // Always update to the new row object so stats reflect the current filter
            setSelectedUser(stillVisible ?? rows[0]);
        } else {
            setSelectedUser(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const totalPages = useMemo(() => {
        if (totalCount <= 0) return 1;
        return Math.ceil(totalCount / PAGE_SIZE);
    }, [totalCount]);

    React.useEffect(() => {
        setPageInput(String(page));
    }, [page]);

    const applyPageFromInput = React.useCallback(() => {
        const raw = pageInput.trim();
        if (raw === "") {
            setPageInput(String(page));
            return;
        }
        const n = Number.parseInt(raw, 10);
        if (!Number.isFinite(n) || n < 1) {
            setPageInput(String(page));
            return;
        }
        const clamped = Math.min(n, totalPages);
        setPage(clamped);
        setPageInput(String(clamped));
    }, [pageInput, page, totalPages]);

    const handleExportExcel = async () => {
        if (exportLoading) return;
        try {
            setExportLoading(true);
            const allRows: UserDirectoryItem[] = [];
            let exportPage = 1;
            let exportTotalCount = 0;

            while (true) {
                const response = await fetchUserDirectory({
                    userName,
                    departments: selectedDepartments,
                    page: exportPage,
                    pageSize: EXPORT_PAGE_SIZE,
                    startTime: startDate,
                    endTime: endDate,
                    agentIds,
                });
                if (exportPage === 1) exportTotalCount = response.totalCount ?? 0;
                allRows.push(...(response.items ?? []));
                if (allRows.length >= exportTotalCount || (response.items ?? []).length === 0) break;
                exportPage += 1;
            }

            const headers = [
                "Display Name",
                "Email",
                "Job Title",
                "Department",
                "Phone Number",
                "Branch",
                "City",
                "Region",
                "Devices",
                "Calls Made",
                "Calls Received",
                "Meeting Joined",
                "Meeting Organized",
                "Voicemail",
            ];
            const rowsForExport = allRows.map((row) => [
                row.displayName ?? "",
                row.mail ?? "",
                row.jobTitle ?? "",
                row.department ?? "",
                row.phoneNumber ?? "",
                row.branch ?? "",
                row.city ?? "",
                row.region ?? "",
                formatDevicesForExport(row.device),
                row.callsMade ?? 0,
                row.callsReceived ?? 0,
                row.meetingJoined ?? 0,
                row.meetingOrganized ?? 0,
                row.voicemail ?? 0,
            ]);

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rowsForExport]);
            worksheet["!cols"] = [
                { wch: 28 }, { wch: 32 }, { wch: 32 }, { wch: 24 },
                { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 12 }, { wch: 48 },
                { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 16 }, { wch: 10 },
            ];
            const fileDate = new Date().toISOString().split("T")[0];
            XLSX.utils.book_append_sheet(workbook, worksheet, "User Directory");
            XLSX.writeFile(workbook, `user-directory-${fileDate}.xlsx`);
        } catch (err) {
            console.error("Failed to export user directory:", err);
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <div className="mt-6 mb-4 flex h-[min(760px,max(280px,calc(100dvh-240px)))] min-h-0 flex-col overflow-hidden rounded-xl border border-border/80 bg-white text-[13px] leading-5 shadow-sm dark:border-[#122638] dark:bg-[#071827]">
            <style>{`
                @keyframes slideInFromRight {
                    from { opacity: 0; transform: translateX(24px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideInFromLeft {
                    from { opacity: 0; transform: translateX(-24px); }
                    to   { opacity: 1; transform: translateX(0); }
                }
            `}</style>
            {/* ── Top header bar ── */}
            <div className="flex flex-col gap-2 border-b px-3 py-2 sm:px-4 sm:py-2.5 md:flex-row md:items-center md:justify-between dark:border-[#122638] shrink-0">
                <div className="space-y-0.5">
                    <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                        Users Directory
                    </h3>
                    <p className="text-xs text-muted-foreground">
                        Detail information about Teams phone users
                    </p>
                </div>
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                    <DepartmentFilter
                        value={selectedDepartments}
                        onChange={setSelectedDepartments}
                        departments={availableDepartments}
                        placeholder={departmentsLoading ? "Loading departments..." : "All Departments"}
                    />
                    <SearchInput
                        placeholder="Search users..."
                        onSearch={setUserName}
                        debounceMs={500}
                        initialValue={userName}
                    />
                    <Button
                        variant="outline"
                        className="h-8 text-xs flex items-center gap-1 [text-decoration:none] rounded-full border-slate-300 bg-white px-3 hover:bg-slate-900 hover:text-white dark:bg-[#0B2030] dark:border-[#2A455A] dark:text-white dark:hover:bg-[#00FEC1] dark:hover:border-[#00FEC1] dark:hover:text-black"
                        onClick={handleExportExcel}
                        disabled={exportLoading || loading}
                    >
                        {exportLoading ? (
                            <>
                                <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <Download className="h-3 w-3" />
                                Export
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* ── Body: left user list + right detail panel; footer spans full width (mock). ── */}
            <div className="flex min-h-0 flex-1 flex-col">
                <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col md:flex-row">
                    {/* Left panel – user card list */}
                    <div className="flex w-full min-h-0 shrink-0 flex-col border-b border-border/80 dark:border-[#122638] md:max-h-none md:shrink-0 md:flex-[0_0_30%] md:border-b-0 md:border-r max-h-[min(240px,38dvh)]">
                        <div className="min-h-0 flex-1 overflow-y-auto p-1.5 sm:p-2 space-y-0.5">
                            {loading ? (
                                <div className="py-10 text-center text-xs text-muted-foreground">
                                    Loading…
                                </div>
                            ) : error ? (
                                <div className="py-10 text-center text-xs text-red-500">{error}</div>
                            ) : rows.length === 0 ? (
                                <div className="py-10 text-center text-xs text-muted-foreground">
                                    No users found
                                </div>
                            ) : (
                                rows.map((user) => (
                                    <UserCard
                                        key={user.id}
                                        user={user}
                                        isSelected={selectedUser?.id === user.id}
                                        onClick={() => setSelectedUser(user)}
                                    />
                                ))
                            )}
                        </div>
                    </div>

                    {/* Right panel — table → profile → stat widgets */}
                    <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col md:flex-[0_0_70%]">
                        <UserDirectoryTable
                            rows={rows}
                            loading={loading}
                            error={error}
                            selectedUserId={selectedUser?.id}
                            tableScrollRef={tableScrollRef}
                            canPanTableX={canPanTableX}
                            isPanningTable={isPanningTable}
                            onTablePointerDown={handleTablePointerDown}
                            onTablePointerMove={handleTablePointerMove}
                            onTablePointerUp={endTablePan}
                            onTablePointerCancel={endTablePan}
                            onTableLostPointerCapture={handleTableLostPointerCapture}
                        />

                        {loading ? (
                            <div className="shrink-0 border-y dark:border-[#122638]">
                                <div className="bg-gradient-to-r from-slate-100 via-blue-50 to-blue-100 dark:from-[#0E2636] dark:via-[#0A2E4A] dark:to-[#0A3050] px-3 py-2.5 sm:px-4 sm:py-3 flex items-center gap-3">
                                    <div className="h-11 w-11 sm:h-12 sm:w-12 flex-shrink-0 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
                                    <div className="flex flex-1 flex-col gap-2">
                                        <div className="h-4 w-40 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                                        <div className="h-3 w-56 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                                        <div className="mt-1 flex gap-2">
                                            <div className="h-5 w-20 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
                                            <div className="h-5 w-24 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : selectedUser ? (
                            <UserDirectoryProfileHeader user={selectedUser} />
                        ) : (
                            <div className="shrink-0 border-y px-4 py-6 text-center text-xs text-muted-foreground dark:border-[#122638]">
                                Select a user to view details
                            </div>
                        )}

                        {!loading && selectedUser ? <UserDirectoryStatWidgets user={selectedUser} /> : null}

                        {loading ? (
                            <div className="shrink-0 border-t bg-white dark:border-[#122638] dark:bg-[#071827]">
                                <div className="grid grid-cols-4 divide-x divide-border/80 items-stretch dark:divide-[#122638]">
                                    {[...Array(4)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="flex h-full min-h-0 min-w-0 flex-col gap-1.5 px-1 py-[0.65rem] sm:px-2 sm:py-[0.65rem]"
                                        >
                                            <div className="flex w-full shrink-0 items-center justify-center gap-1.5">
                                                <div className="h-4 w-4 shrink-0 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                                                <div className="h-2.5 w-12 max-w-[min(100%,4.5rem)] rounded bg-slate-200 dark:bg-slate-700 animate-pulse sm:w-14" />
                                            </div>
                                            <div className="flex flex-1 flex-col items-center justify-center">
                                                <div className="h-5 w-7 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>

                <div className="flex shrink-0 items-center justify-between border-t border-slate-200/90 bg-slate-50/80 px-3 py-2 text-xs text-muted-foreground dark:border-[#122638] dark:bg-[#0a2233] sm:px-4">
                    <span className="text-slate-600 dark:text-slate-300">{totalCount} users</span>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            className="grid h-7 w-7 place-items-center rounded border border-slate-300 bg-white transition-colors hover:bg-slate-100 disabled:opacity-40 dark:border-[#2A455A] dark:bg-[#0B2030] dark:hover:bg-[#122638]"
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            disabled={loading || page <= 1}
                            aria-label="Previous page"
                        >
                            <ChevronLeft className="h-3.5 w-3.5" />
                        </button>
                        <div className="flex items-center gap-0.5 tabular-nums text-slate-700 dark:text-slate-200">
                            <input
                                type="text"
                                inputMode="numeric"
                                autoComplete="off"
                                aria-label="Go to page"
                                title={`Page 1–${totalPages}`}
                                disabled={loading}
                                value={pageInput}
                                onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, "");
                                    setPageInput(digits);
                                }}
                                onBlur={applyPageFromInput}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        applyPageFromInput();
                                        (e.target as HTMLInputElement).blur();
                                    }
                                }}
                                className="h-7 w-9 rounded border border-slate-300 bg-white px-0.5 text-center text-xs font-medium text-slate-800 outline-none ring-offset-1 focus:border-slate-500 focus:ring-1 focus:ring-slate-400 disabled:opacity-50 dark:border-[#2A455A] dark:bg-[#0B2030] dark:text-slate-100 dark:focus:border-slate-400 dark:focus:ring-slate-500"
                            />
                            <span className="text-xs font-medium">/ {totalPages}</span>
                        </div>
                        <button
                            type="button"
                            className="grid h-7 w-7 place-items-center rounded border border-slate-300 bg-white transition-colors hover:bg-slate-100 disabled:opacity-40 dark:border-[#2A455A] dark:bg-[#0B2030] dark:hover:bg-[#122638]"
                            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                            disabled={loading || page >= totalPages}
                            aria-label="Next page"
                        >
                            <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDirectorySection;
